"use client";

import {
  AlertTriangle,
  ArrowUpRight,
  Construction,
  ExternalLink,
  LocateFixed,
  RefreshCw,
  Search,
  Shield,
  ShieldAlert,
  TrafficCone,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SituationMap } from "./SituationMap";
import type { Incident, IncidentStatus, Severity } from "./lib/incidents/types";

type ApiResponse = {
  incidents: Incident[];
  health: { astra: "live" | "demo" | "unavailable"; kapo_zh: "live" | "cached" | "unavailable"; notices: string[] };
  fetchedAt: string;
};

type AgeFilter = "1h" | "6h" | "24h" | "all";
const roads = ["Alle", "A1", "A2", "A3", "A4", "A13"];
const ages: Array<{ value: AgeFilter; label: string }> = [
  { value: "1h", label: "1 h" }, { value: "6h", label: "6 h" }, { value: "24h", label: "24 h" }, { value: "all", label: "Alle" },
];

const statusLabels: Record<IncidentStatus, string> = {
  active: "Aktiv", monitoring: "Beobachtung", information: "Information", resolved: "Erledigt", unknown: "Unklar",
};
const sourceLabels = { astra: "ASTRA", kapo_zh: "Kapo ZH" } as const;
const subtypeLabels: Record<string, string> = {
  accident: "Unfall", congestion: "Stau", construction: "Baustelle", closure: "Sperrung", danger: "Gefahr", info: "Hinweis",
};

function formatDateTime(value?: string) {
  if (!value) return "Nicht angegeben";
  return new Intl.DateTimeFormat("de-CH", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Zurich" }).format(new Date(value));
}

function formatAge(value: string) {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 60_000));
  if (minutes < 60) return `vor ${minutes || 1} Min.`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  return `vor ${Math.round(hours / 24)} Tg.`;
}

function roadOf(incident: Incident) {
  return typeof incident.metadata?.road === "string" ? incident.metadata.road : "";
}

function primaryLocation(incident: Incident) {
  return incident.locations.find((location) => location.role === "primary") ?? incident.locations[0];
}

function EventIcon({ incident }: { incident: Incident }) {
  if (incident.category === "police") return <Shield size={18} />;
  if (incident.subtype === "construction") return <Construction size={18} />;
  if (incident.subtype === "congestion") return <TrafficCone size={18} />;
  if (["danger", "closure"].includes(incident.subtype ?? "")) return <AlertTriangle size={18} />;
  return <ShieldAlert size={18} />;
}

export default function Home() {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [road, setRoad] = useState("Alle");
  const [query, setQuery] = useState("");
  const [age, setAge] = useState<AgeFilter>("24h");
  const [minSeverity, setMinSeverity] = useState<Severity>(1);
  const [activeOnly, setActiveOnly] = useState(false);
  const [layers, setLayers] = useState({ traffic: true, police: true });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(true);

  const loadSituation = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/traffic?since=all", { cache: "no-store" });
      if (!response.ok) throw new Error("Lagedaten konnten nicht geladen werden.");
      const next = await response.json() as ApiResponse;
      setData(next);
      setSelectedId((current) => current ?? next.incidents[0]?.id ?? null);
    } catch {
      setData({ incidents: [], health: { astra: "unavailable", kapo_zh: "unavailable", notices: ["Quellen momentan nicht erreichbar"] }, fetchedAt: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const kickoff = window.setTimeout(() => void loadSituation(), 0);
    const timer = window.setInterval(() => void loadSituation(), 120_000);
    return () => { window.clearTimeout(kickoff); window.clearInterval(timer); };
  }, [loadSituation]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("de-CH");
    const referenceTime = data ? Date.parse(data.fetchedAt) : 0;
    const since = age === "all" ? 0 : referenceTime - Number(age.replace("h", "")) * 60 * 60 * 1000;
    return (data?.incidents ?? []).filter((incident) => {
      if (incident.category === "traffic" && !layers.traffic) return false;
      if (incident.category === "police" && !layers.police) return false;
      if (incident.severity < minSeverity) return false;
      if (activeOnly && !["active", "monitoring"].includes(incident.status)) return false;
      const relevantTime = Date.parse(incident.occurredAt ?? incident.publishedAt);
      if (since && relevantTime < since && incident.status !== "active") return false;
      if (road !== "Alle" && (incident.category !== "traffic" || roadOf(incident) !== road)) return false;
      if (!normalized) return true;
      const locationText = incident.locations.map((location) => location.name).join(" ");
      return `${incident.title} ${incident.description ?? ""} ${locationText} ${roadOf(incident)} ${sourceLabels[incident.source as keyof typeof sourceLabels] ?? incident.source}`
        .toLocaleLowerCase("de-CH").includes(normalized);
    });
  }, [activeOnly, age, data, layers, minSeverity, query, road]);

  const effectiveSelectedId = selectedId && filtered.some((incident) => incident.id === selectedId) ? selectedId : filtered[0]?.id ?? null;
  const selected = filtered.find((incident) => incident.id === effectiveSelectedId) ?? null;
  const mapped = filtered.filter((incident) => incident.locations.some((location) => location.geometry));
  const statusText = data ? `ASTRA ${data.health.astra === "live" ? "Live" : data.health.astra === "demo" ? "Demo" : "offline"} · Kapo ${data.health.kapo_zh === "unavailable" ? "offline" : "Live"}` : "Lage wird geladen";

  return (
    <main className="cockpit">
      <header className="topbar">
        <div className="brand"><span className="brand-mark"><span /></span><span>Swiss Situation</span></div>
        <div className="live-state" aria-live="polite"><span className={data?.health.astra === "live" ? "live-dot" : "demo-dot"} />{statusText}<span className="topbar-time">{data ? `· ${formatDateTime(data.fetchedAt).split(", ").at(-1)}` : ""}</span></div>
        <button className="icon-button" type="button" onClick={() => void loadSituation()} aria-label="Lagedaten aktualisieren"><RefreshCw size={17} className={loading ? "spin" : ""} /></button>
      </header>

      <section className={`workspace ${listOpen ? "" : "list-collapsed"}`}>
        <aside className="sidebar">
          <div className="filters">
            <div className="search-wrap"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ort, Strasse oder Meldung" aria-label="Meldungen durchsuchen" />{query && <button type="button" onClick={() => setQuery("")} aria-label="Suche leeren"><X size={14} /></button>}</div>
            <div className="filter-group">
              <span className="filter-label">Ebenen</span>
              <div className="layer-row">
                <button type="button" className={layers.traffic ? "layer-toggle traffic active" : "layer-toggle traffic"} onClick={() => setLayers((value) => ({ ...value, traffic: !value.traffic }))}><TrafficCone size={15} />Verkehr</button>
                <button type="button" className={layers.police ? "layer-toggle police active" : "layer-toggle police"} onClick={() => setLayers((value) => ({ ...value, police: !value.police }))}><Shield size={15} />Polizei</button>
              </div>
            </div>
            <div className="filter-pair">
              <div className="filter-group"><span className="filter-label">Zeitraum</span><div className="compact-row">{ages.map((item) => <button key={item.value} type="button" className={age === item.value ? "compact-filter active" : "compact-filter"} onClick={() => setAge(item.value)}>{item.label}</button>)}</div></div>
              <label className="severity-select"><span className="filter-label">Ab Stufe</span><select value={minSeverity} onChange={(event) => setMinSeverity(Number(event.target.value) as Severity)} aria-label="Minimale Schwere"><option value="1">1</option><option value="2">2</option><option value="3">3</option><option value="4">4</option><option value="5">5</option></select></label>
            </div>
            <label className="active-check"><input type="checkbox" checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} /><span>Nur aktiv / Beobachtung</span></label>
            <div className="chip-row" aria-label="Autobahn filtern">{roads.map((item) => <button key={item} type="button" className={road === item ? "chip active" : "chip"} onClick={() => setRoad(item)}>{item}</button>)}</div>
          </div>

          <div className="list-heading"><span>{filtered.length} Meldungen</span>{data?.health.notices[0] && <span className="notice">{data.health.notices[0]}</span>}</div>
          <div className="event-list">
            {loading && !data ? <div className="loading-list">Aktuelle Lage wird geladen…</div> : filtered.length ? filtered.map((incident) => {
              const location = primaryLocation(incident);
              const roadLabel = roadOf(incident);
              return <button key={incident.id} type="button" className={`event-card status-${incident.status} ${effectiveSelectedId === incident.id ? "selected" : ""}`} onClick={() => setSelectedId(incident.id)}>
                <span className={`severity severity-${incident.severity}`} />
                <span className={`event-icon ${incident.category}`}><EventIcon incident={incident} /></span>
                <span className="event-copy"><span className="event-kicker"><strong>{roadLabel || sourceLabels[incident.source as keyof typeof sourceLabels] || incident.source}</strong><span>{incident.category === "traffic" ? subtypeLabels[incident.subtype ?? ""] ?? "Verkehr" : statusLabels[incident.status]}</span><time>{formatAge(incident.occurredAt ?? incident.publishedAt)}</time></span><span className="event-title">{incident.title}</span><span className="event-location">{location?.name ?? "Nicht exakt verortet"}{location?.precision === "municipality" ? " · Gemeindegebiet" : ""}</span></span>
                <ArrowUpRight size={15} className="event-arrow" />
              </button>;
            }) : <div className="empty-state"><LocateFixed size={22} /><strong>Keine Treffer</strong><span>Filter oder Zeitraum anpassen.</span></div>}
          </div>
        </aside>

        <section className="map-panel" aria-label="Sicherheits- und Verkehrslage Schweiz">
          <SituationMap incidents={filtered} selectedId={effectiveSelectedId} selectedRoad={road} onSelect={setSelectedId} />
          <div className="map-meta"><span>Schweiz · Lagebild</span><span>{mapped.length} kartiert · {filtered.length - mapped.length} ohne belastbare Position</span></div>
          {selected && (() => {
            const location = primaryLocation(selected);
            const roadLabel = roadOf(selected);
            return <article className={`detail-card status-${selected.status}`}>
              <button className="detail-close" type="button" onClick={() => setSelectedId(null)} aria-label="Detail schliessen"><X size={16} /></button>
              <div className="detail-kicker"><span className={`severity severity-${selected.severity}`} /><strong>{sourceLabels[selected.source as keyof typeof sourceLabels] ?? selected.source}</strong><span>{statusLabels[selected.status]} · Stufe {selected.severity}</span></div>
              <h1>{selected.title}</h1><p>{selected.description}</p>
              <div className="detail-grid">
                <div><span>Ort</span><strong>{location?.name ?? "Nicht kartiert"}</strong><small>{location ? location.precision === "municipality" ? "Nur auf Gemeindeebene" : location.precision === "street" ? "Strassenebene" : location.precision === "exact" ? "Exakte Quelldaten" : "Präzision unbekannt" : "Keine belastbare Position"}</small></div>
                <div><span>Ereignis</span><strong>{formatDateTime(selected.occurredAt)}</strong><small>Publiziert {formatDateTime(selected.publishedAt)}</small></div>
              </div>
              {roadLabel && <div className="detail-meta"><TrafficCone size={15} /><span>{roadLabel}{typeof selected.metadata?.direction === "string" && selected.metadata.direction ? ` · ${selected.metadata.direction}` : ""}</span></div>}
              <a className="source-link" href={selected.canonicalSourceUrl} target="_blank" rel="noreferrer">Offizielle Quelle öffnen <ExternalLink size={14} /></a>
            </article>;
          })()}
          <button className="mobile-list-toggle" type="button" onClick={() => setListOpen((value) => !value)}>{listOpen ? "Karte" : `${filtered.length} Meldungen`}</button>
          <div className="attribution">Daten: VDP ASTRA · Kantonspolizei Zürich · Karte © OpenFreeMap / OpenStreetMap</div>
        </section>
      </section>
    </main>
  );
}
